// Utils
import {
    allSelectedElementsCanToggleInfo,
    allSelectedElementsOnCanvas,
    commentsFeedbackOtherwiseAll,
    feedbackRequired,
    noSelectedElementsInIconViewMode,
} from '../toolbarToolConfigUtils';
import fileGetMediaPlayerType from '../../../../element/file/utils/fileGetMediaPlayerType';
import { isLocationAttachedInline } from '../../../../../common/elements/utils/elementLocationUtils';
import { getElementType } from '../../../../../common/elements/utils/elementPropertyUtils';
import { ElementType } from '../../../../../common/elements/elementTypes';
import { getSelectedElementTypes } from '../../utils/toolbarAvailableToolsUtils';
import { isFeatureEnabled } from '../../../../../common/users/utils/userPropertyUtils';
import { isImageBackgroundToolAvailable } from './imageBackgroundToolAvailability';

// Components
import DesktopColorTool from '../../components/selectionTools/colorTools/DesktopColorTool';
import MobileColorTool from '../../components/selectionTools/colorTools/popup/mobile/MobileColorTool';
import TitleTool from '../../components/selectionTools/titleTool/TitleTool';
import CaptionTool from '../../components/selectionTools/captionTool/CaptionTool';
import ReactionTool from '../../components/selectionTools/reactionTool/ReactionTool';
import MobileReactionTool from '../../components/selectionTools/reactionTool/MobileReactionTool';
import ElementLabelTool from '../../components/selectionTools/labelsTool/popup/ElementLabelsTool';
import IconTool from '../../components/selectionTools/iconTools/IconTool';
import MobileIconTool from '../../components/selectionTools/iconTools/mobile/MobileIconTool';
import MediaTool from '../../components/selectionTools/attachmentTool/MediaTool';
import MediaLoopTool from '../../components/selectionTools/attachmentTool/MediaLoopTool';
import MediaAutoplayTool from '../../components/selectionTools/attachmentTool/MediaAutoplayTool';
import DisplayModeTool from '../../components/selectionTools/displayModeTool/DisplayModeTool';
import ImageEditTool from '../../components/selectionTools/imageEditTool/ImageEditTool';
import ImageBackgroundTool from '../../components/selectionTools/imageBackgroundTool/ImageBackgroundTool';
import ShowBackgroundTool from '../../components/selectionTools/showBackgroundTool/ShowBackgroundTool';
import CustomColorTool from '../../components/selectionTools/colorTools/CustomColorTool';
import StartArrowTool from '../../components/selectionTools/arrowTools/StartArrowTool';
import EndArrowTool from '../../components/selectionTools/arrowTools/EndArrowTool';
import LineStyleTool from '../../components/selectionTools/lineTools/LineStyleTool';
import LineWeightTool from '../../components/selectionTools/lineTools/LineWeightTool';
import LineLabelTool from '../../components/selectionTools/lineTools/LineLabelTool';
import CollapseTool from '../../components/selectionTools/commentTools/CollapseTool';
import EditDrawingTool from '../../components/selectionTools/editDrawingTool/EditDrawingTool';
import AlignmentTool from '../../components/selectionTools/arrangementTools/alignmentTools/AlignmentTool';
import DistributeTool from '../../components/selectionTools/arrangementTools/distributeTools/DistributeTool';
import ConnectTool from '../../components/selectionTools/connectTool/ConnectTool';
import ColorDisplayTool from '../../components/selectionTools/colorDisplayTool/ColorDisplayTool';
import MobileContextMenuTool from '../../components/mobileTools/mobileContextMenu/MobileContextMenuTool';
import MobileColorDisplayTool from '../../components/selectionTools/colorDisplayTool/MobileColorDisplayTool';
import MobileLineWeightTool from '../../components/selectionTools/lineTools/MobileLineWeightTool';
import RemoveElementTool from '../../components/selectionTools/removeElementTool/RemoveElementTool';

// Constants
import { COLLAPSE_ORDER, TOOL_HEIGHT } from '../toolbarToolConfigConstants';
import { MEDIA_PLAYER_TYPES } from '../../../../element/file/mediaPlayer/mediaPlayerConstants';
import { CURRENT_BOARD_HEADER_TITLE_EDITOR_KEY } from '../../../../../common/boards/boardConstants';
import { ALIGNMENT_MIN_ELEMENTS } from '../../components/selectionTools/arrangementTools/alignmentTools/alignmentConstants';
import { DISTRIBUTION_MIN_ELEMENTS } from '../../components/selectionTools/arrangementTools/distributeTools/distributionConstants';
import { ExperimentId } from '../../../../../common/experiments/experimentsConstants';

// Types
import { ToolbarItemConfig } from '../../toolbarTypes';
import { MilanoteApplicationMode } from '../../../../../common/platform/platformTypes';
import CommentTool from '../../components/selectionTools/commentTools/CommentTool';
import DetachCommentTool from '../../components/selectionTools/attachedCommentTools/DetachCommentTool';
import DisplayInfoTool from '../../components/selectionTools/displayInfoTool/DisplayInfoTool';
import MobileElementLabelsTool from '../../components/selectionTools/labelsTool/sheet/MobileElementLabelsTool';
import RenameTool from '../../components/selectionTools/renameTool/RenameTool';
import LabelColorTool from '../../components/selectionTools/labelsTool/components/LabelColorTool';
import MobileLabelColorTool from '../../components/selectionTools/labelsTool/sheet/MobileLabelColorTool';
import DesktopElementContextMenuTool, {
    DESKTOP_ELEMENT_CONTEXT_MENU_TOOL_ID,
} from '../../components/selectionTools/contextMenuTool/DesktopElementContextMenuTool';

const COLOR: ToolbarItemConfig = {
    id: 'COLOR',
    name: 'Color',
    localeKey: 'tools.selected.color',
    component: DesktopColorTool,
    mobileComponent: MobileColorTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: true,
    permissionPredicate: commentsFeedbackOtherwiseAll,
    availabilityPredicate: ({ editorKey }) => editorKey !== CURRENT_BOARD_HEADER_TITLE_EDITOR_KEY,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const TITLE: ToolbarItemConfig = {
    id: 'TITLE',
    name: 'Title',
    localeKey: 'tools.selected.title',
    component: TitleTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: true,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const DISPLAY_INFO: ToolbarItemConfig = {
    id: 'DISPLAY_INFO',
    name: 'Display info',
    localeKey: 'tools.selected.fileInfo',
    component: DisplayInfoTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: true,
    availabilityPredicate: ({ selectedElements }) =>
        getSelectedElementTypes(selectedElements).size === 1 &&
        allSelectedElementsCanToggleInfo(selectedElements) &&
        noSelectedElementsInIconViewMode(selectedElements),
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const CAPTION: ToolbarItemConfig = {
    id: 'CAPTION',
    name: 'Caption',
    localeKey: 'tools.selected.caption',
    component: CaptionTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: true,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const REACTION: ToolbarItemConfig = {
    id: 'REACTION',
    name: 'Add reaction',
    localeKey: 'tools.selected.addReaction',
    component: ReactionTool,
    mobileComponent: MobileReactionTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: true,
    permissionPredicate: feedbackRequired,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const LABEL: ToolbarItemConfig = {
    id: 'LABEL',
    name: 'Add labels',
    localeKey: 'tools.selected.label',
    component: ElementLabelTool,
    mobileComponent: MobileElementLabelsTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: true,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const LABEL_COLOR: ToolbarItemConfig = {
    id: 'LABEL_COLOR',
    name: 'Color',
    localeKey: 'tools.selected.color',
    component: LabelColorTool,
    mobileComponent: MobileLabelColorTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: true,
    permissionPredicate: commentsFeedbackOtherwiseAll,
    availabilityPredicate: ({ editorKey }) => editorKey !== CURRENT_BOARD_HEADER_TITLE_EDITOR_KEY,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const LABEL_RENAME: ToolbarItemConfig = {
    id: 'LABEL_RENAME',
    name: 'Rename',
    localeKey: 'tools.selected.rename',
    component: RenameTool,
    toolSettings: { isLabel: true },
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: false,
    permissionPredicate: commentsFeedbackOtherwiseAll,
    availabilityPredicate: ({ editorKey }) => editorKey !== CURRENT_BOARD_HEADER_TITLE_EDITOR_KEY,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const LABEL_MOVE_TO_TRASH: ToolbarItemConfig = {
    id: 'LABEL_MOVE_TO_TRASH',
    name: 'Remove',
    localeKey: 'tools.selected.moveToTrash',
    component: RemoveElementTool,
    toolSettings: { isLabel: true },
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: true,
    permissionPredicate: commentsFeedbackOtherwiseAll,
    availabilityPredicate: ({ editorKey }) => editorKey !== CURRENT_BOARD_HEADER_TITLE_EDITOR_KEY,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const COMMENT: ToolbarItemConfig = {
    id: 'COMMENT',
    name: 'Comment',
    localeKey: 'tools.selected.comment',
    component: CommentTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: false,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const ICON: ToolbarItemConfig = {
    id: 'ICON',
    name: 'Icon',
    localeKey: 'tools.selected.icon',
    component: IconTool,
    mobileComponent: MobileIconTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: false,
    availabilityPredicate: ({ editorKey }) => editorKey !== CURRENT_BOARD_HEADER_TITLE_EDITOR_KEY,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const MEDIA: ToolbarItemConfig = {
    id: 'MEDIA',
    name: 'Media',
    localeKey: 'tools.selected.media',
    component: MediaTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: false,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const MEDIA_LOOP: ToolbarItemConfig = {
    id: 'MEDIA_LOOP',
    name: 'Loop',
    localeKey: 'tools.selected.loop',
    component: MediaLoopTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: true,
    availabilityPredicate: ({ selectedElements }) =>
        selectedElements.every(
            (element) =>
                fileGetMediaPlayerType(element) === MEDIA_PLAYER_TYPES.AUDIO ||
                fileGetMediaPlayerType(element) === MEDIA_PLAYER_TYPES.VIDEO,
        ),
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const MEDIA_AUTOPLAY: ToolbarItemConfig = {
    id: 'MEDIA_AUTOPLAY',
    name: 'Autoplay',
    localeKey: 'tools.selected.autoplay',
    component: MediaAutoplayTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: true,
    availabilityPredicate: ({ selectedElements }) =>
        selectedElements.every(
            (element) =>
                fileGetMediaPlayerType(element) === MEDIA_PLAYER_TYPES.AUDIO ||
                fileGetMediaPlayerType(element) === MEDIA_PLAYER_TYPES.VIDEO,
        ),
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const DISPLAY_MODE: ToolbarItemConfig = {
    id: 'DISPLAY_MODE',
    name: 'Preview',
    localeKey: 'tools.selected.preview',
    component: DisplayModeTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: true,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const RENAME_SUPPORTED_ELEMENT_TYPES = new Set<ElementType>([
    ElementType.LINK_TYPE,
    ElementType.BOARD_TYPE,
    ElementType.ALIAS_TYPE,
    ElementType.COLUMN_TYPE,
    ElementType.DOCUMENT_TYPE,
    ElementType.FILE_TYPE,
]);

const RENAME: ToolbarItemConfig = {
    id: 'RENAME',
    name: 'Rename',
    localeKey: 'tools.selected.rename',
    component: RenameTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: false,
    permissionPredicate: commentsFeedbackOtherwiseAll,
    availabilityPredicate: ({ selectedElements }) => {
        if (selectedElements.size !== 1) return false;
        const element = selectedElements.first();
        if (!element) return false;
        return RENAME_SUPPORTED_ELEMENT_TYPES.has(getElementType(element));
    },
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const IMAGE_EDIT: ToolbarItemConfig = {
    id: 'IMAGE_EDIT',
    name: 'Edit',
    localeKey: 'tools.selected.editImage',
    component: ImageEditTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: false,
    availabilityPredicate: ({ currentUser }) => isFeatureEnabled(ExperimentId.imageModalV2, currentUser),
    appModeAvailability: [MilanoteApplicationMode.desktop],
};

const IMAGE_BACKGROUND: ToolbarItemConfig = {
    id: 'IMAGE_BACKGROUND',
    name: 'Background',
    component: ImageBackgroundTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: false,
    availabilityPredicate: isImageBackgroundToolAvailable,
    appModeAvailability: [MilanoteApplicationMode.desktop],
};

const SHOW_BACKGROUND: ToolbarItemConfig = {
    id: 'SHOW_BACKGROUND',
    name: 'Transparent',
    localeKey: 'tools.selected.transparent',
    component: ShowBackgroundTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: true,
    availabilityPredicate: ({ selectedElements, currentUser }) => {
        const selectedElement = selectedElements.first();
        const singleSelectedElementIsImage =
            selectedElements.size === 1 && getElementType(selectedElement) === ElementType.IMAGE_TYPE;
        if (!singleSelectedElementIsImage) return true;

        return !isFeatureEnabled(ExperimentId.imageModalV2, currentUser);
    },
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const CUSTOM_COLOR: ToolbarItemConfig = {
    id: 'CUSTOM_COLOR',
    name: 'Color',
    localeKey: 'tools.selected.color',
    component: CustomColorTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: true,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const COLOR_DISPLAY: ToolbarItemConfig = {
    id: 'COLOR_DISPLAY',
    name: 'Display',
    localeKey: 'tools.selected.display',
    component: ColorDisplayTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: true,
    mobileComponent: MobileColorDisplayTool,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const START_ARROW: ToolbarItemConfig = {
    id: 'START_ARROW',
    name: 'Start',
    localeKey: 'tools.selected.start',
    component: StartArrowTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: true,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const END_ARROW: ToolbarItemConfig = {
    id: 'END_ARROW',
    name: 'End',
    localeKey: 'tools.selected.end',
    component: EndArrowTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: true,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const LINE_STYLE: ToolbarItemConfig = {
    id: 'LINE_STYLE',
    name: 'Dashed',
    localeKey: 'tools.selected.line.dashed',
    component: LineStyleTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: true,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const LINE_WEIGHT: ToolbarItemConfig = {
    id: 'LINE_WEIGHT',
    name: 'Weight',
    localeKey: 'tools.selected.line.weight',
    component: LineWeightTool,
    mobileComponent: MobileLineWeightTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: true,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const LINE_LABEL: ToolbarItemConfig = {
    id: 'LINE_LABEL',
    name: 'Label',
    localeKey: 'tools.selected.line.label',
    component: LineLabelTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: false,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const COLLAPSE: ToolbarItemConfig = {
    id: 'COLLAPSE',
    name: 'Collapse',
    localeKey: 'tools.selected.collapse',
    component: CollapseTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: true,
    permissionPredicate: feedbackRequired,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const EDIT_DRAWING: ToolbarItemConfig = {
    id: 'EDIT_DRAWING',
    name: 'Edit drawing',
    localeKey: 'tools.selected.editDrawing',
    component: EditDrawingTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: false,
    appModeAvailability: [MilanoteApplicationMode.desktop],
};

const ALIGNMENT: ToolbarItemConfig = {
    id: 'ALIGNMENT',
    name: 'Alignment',
    localeKey: 'tools.selected.alignment',
    component: AlignmentTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: true,
    availabilityPredicate: ({ selectedElements }) =>
        selectedElements.size >= ALIGNMENT_MIN_ELEMENTS && allSelectedElementsOnCanvas(selectedElements),
    appModeAvailability: [MilanoteApplicationMode.desktop],
};

const DISTRIBUTE: ToolbarItemConfig = {
    id: 'DISTRIBUTE',
    name: 'Distribute',
    localeKey: 'tools.selected.distribute',
    component: DistributeTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: true,
    availabilityPredicate: ({ selectedElements }) =>
        selectedElements.size >= DISTRIBUTION_MIN_ELEMENTS && allSelectedElementsOnCanvas(selectedElements),
    appModeAvailability: [MilanoteApplicationMode.desktop],
};

const CONNECT_WITH_LINES: ToolbarItemConfig = {
    id: 'CONNECT_WITH_LINES',
    name: 'Connect',
    localeKey: 'tools.selected.connect',
    component: ConnectTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: true,
    availabilityPredicate: ({ selectedElements }) =>
        selectedElements.size > 1 && allSelectedElementsOnCanvas(selectedElements),
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const DESKTOP_CONTEXT_MENU: ToolbarItemConfig = {
    id: DESKTOP_ELEMENT_CONTEXT_MENU_TOOL_ID,
    name: 'More',
    localeKey: 'tools.selected.more',
    component: DesktopElementContextMenuTool,
    height: {
        large: 16,
        medium: 16,
        small: 14,
    },
    collapseOrder: COLLAPSE_ORDER.EARLY,
    collapseBehavior: 'hide',
    multiSelect: true,
    availabilityPredicate: ({ currentUser }) => isFeatureEnabled(ExperimentId.contextMenuV2, currentUser),
    appModeAvailability: [MilanoteApplicationMode.desktop],
};

const MOBILE_CONTEXT_MENU: ToolbarItemConfig = {
    id: 'MOBILE_CONTEXT_MENU',
    name: 'More',
    localeKey: 'tools.selected.more',
    component: MobileContextMenuTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: true,
    availabilityPredicate: ({ selectedElements, editorKey }) =>
        editorKey !== CURRENT_BOARD_HEADER_TITLE_EDITOR_KEY && !selectedElements.some(isLocationAttachedInline),
    appModeAvailability: [MilanoteApplicationMode.mobile],
};

const MOVE_TO_TRASH: ToolbarItemConfig = {
    id: 'MOVE_TO_TRASH',
    name: 'Remove',
    localeKey: 'tools.selected.moveToTrash',
    component: RemoveElementTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: true,
    availabilityPredicate: ({ selectedElements }) => selectedElements.every(isLocationAttachedInline),
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const DETACH_COMMENT: ToolbarItemConfig = {
    id: 'DETACH_COMMENT',
    name: 'Detach',
    localeKey: 'tools.selected.detachComment',
    component: DetachCommentTool,
    height: TOOL_HEIGHT.SQUARE,
    multiSelect: true,
    availabilityPredicate: ({ selectedElements }) => selectedElements.every(isLocationAttachedInline),
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

export const SELECTED_LABELS_TOOLS = {
    DEFAULT: [LABEL_COLOR, LABEL_RENAME, LABEL_MOVE_TO_TRASH],
};

export default {
    COLOR,
    TITLE,
    DISPLAY_INFO,
    CAPTION,
    REACTION,
    RENAME,
    LABEL,
    COMMENT,
    ICON,
    MEDIA,
    MEDIA_LOOP,
    MEDIA_AUTOPLAY,
    DISPLAY_MODE,
    IMAGE_EDIT,
    IMAGE_BACKGROUND,
    SHOW_BACKGROUND,
    CUSTOM_COLOR,
    COLOR_DISPLAY,
    START_ARROW,
    END_ARROW,
    LINE_STYLE,
    LINE_WEIGHT,
    LINE_LABEL,
    COLLAPSE,
    EDIT_DRAWING,
    ALIGNMENT,
    DISTRIBUTE,
    CONNECT_WITH_LINES,
    DESKTOP_CONTEXT_MENU,
    MOBILE_CONTEXT_MENU,
    MOVE_TO_TRASH,
    DETACH_COMMENT,
} as const;
